/* eslint-disable i18next/no-literal-string */
import { useEffect, useRef, useState } from 'react';

export default function BrowserExpressionProbe() {
  const worker = useRef<Worker | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(
    'Not run. This tests only the small appearance stage, not the complete filter.',
  );
  const [backend, setBackend] = useState('wasm');
  const [stage, setStage] = useState('appearance');
  const [fixtureFile, setFixtureFile] = useState<File | null>(null);
  useEffect(
    () => () => {
      worker.current?.terminate();
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  function stop() {
    worker.current?.terminate();
    worker.current = null;
    if (timer.current) clearTimeout(timer.current);
    setBusy(false);
  }
  async function run() {
    stop();
    setBusy(true);
    setStatus('Starting browser worker…');
    const instance =
      backend === 'wasm'
        ? new Worker(new URL('../lib/expressionProbeWasm.worker.ts', import.meta.url), {
            type: 'module',
          })
        : new Worker(new URL('../lib/expressionProbe.worker.ts', import.meta.url), {
            type: 'module',
          });
    worker.current = instance;
    instance.onmessage = (event) => {
      if (worker.current !== instance) return;
      setStatus(JSON.stringify(event.data, null, 2));
      if (event.data.done) stop();
    };
    instance.onerror = (event) => {
      if (worker.current !== instance) return;
      setStatus(`Browser worker failed: ${event.message}`);
      stop();
    };
    timer.current = setTimeout(
      () => {
        stop();
        setStatus('Stopped at the time limit. This device did not complete the probe.');
      },
      stage.startsWith('renderer') ? 180_000 : 90_000,
    );
    try {
      const fixture = import.meta.env.DEV && fixtureFile && stage.startsWith('renderer')
        ? JSON.parse(await fixtureFile.text()) : undefined;
      if (worker.current === instance) instance.postMessage({ backend, stage, fixture });
    } catch {
      if (worker.current === instance) {
        setStatus('Could not read the local fixture JSON.');
        stop();
      }
    }
  }
  return (
    <section>
      <h2>Phase 8 — Browser-only feasibility probe (v3)</h2>
      <p>
        No photo upload, Python worker or reference image. Downloads the selected model plus the
        browser runtime automatically. Uses synthetic input and checks the output against the
        desktop baseline.
      </p>
      <p>
        This is not the crying filter yet. Its timing cannot be extrapolated to the much larger
        renderer.
      </p>
      <label>
        Model stage{' '}
        <select disabled={busy} value={stage} onChange={(e) => setStage(e.target.value)}>
          <option value="appearance">Appearance — 3.2 MiB</option>
          <option value="motion">Motion/expression — 107.4 MiB (use Wi-Fi)</option>
          {import.meta.env.DEV ? (
            <option value="renderer-storage-fp16">Renderer compressed — 257 MiB (desktop research only)</option>
          ) : null}
          {import.meta.env.DEV ? (
            <option value="renderer">Renderer — 507.4 MiB (desktop research only)</option>
          ) : null}
        </select>
      </label>{' '}
      <p>
        Safari GPU shader failure remains unresolved. Start with WebAssembly; motion is a separate
        compatibility test, not a full-filter speed estimate.
      </p>
      <label>
        Runtime{' '}
        <select disabled={busy} value={backend} onChange={(e) => setBackend(e.target.value)}>
          <option value="wasm">WebAssembly (CPU)</option>
          <option value="webgpu">WebGPU (GPU compatibility test)</option>
        </select>
      </label>{' '}
      {import.meta.env.DEV ? (
        <label>
          Local renderer fixture (optional, stays on device){' '}
          <input type="file" accept=".json" disabled={busy}
            onChange={(e) => setFixtureFile(e.target.files?.[0] ?? null)} />
        </label>
      ) : null}
      <button type="button" disabled={busy} onClick={run}>
        Run on this device
      </button>{' '}
      {busy ? (
        <button
          type="button"
          onClick={() => {
            stop();
            setStatus('Cancelled.');
          }}
        >
          Cancel
        </button>
      ) : null}
      <pre role="status" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        {status}
      </pre>
    </section>
  );
}
